/* eslint-disable react/no-unknown-property */
import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { CircularProgress, IconButton } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { MonthlyActivityService } from '/src/api';
import { BarChart, Box } from '/src/components';
import { useDateStore } from '/src/context';
import { Company, Lender, Loan } from '/src/lib/models';
import { BarChartFilters, ChartColors, DateRangeType, TGetMonthlyActivityReportRequest } from '/src/lib/types';

export type MonthlyActivityProps = {
  /**
   * Filter data by the given Company
   */
  companyFilter?: Partial<Company>;
  /**
   * Filter data by the given Loan
   */
  loanFilter?: Partial<Loan>;
  /**
   * Filter data by the given Lender
   */
  lenderFilter?: Partial<Lender>;
};

export const MonthlyActivity: React.FC<MonthlyActivityProps> = observer(
  ({ companyFilter, loanFilter, lenderFilter }) => {
    /** Context **/
    const dateStore = useDateStore();

    /** Refs **/
    const chartColors: ChartColors = {
      advances: ['#316399', '#5C6BC0', '#7986CB', '#9FA8DA', '#C5CAE9'],
      revenue: ['#EAAD47', '#FFCA28', '#FFD54F', '#FFE082', '#FFECB3'],
      principalPayments: ['#4CAF50', '#66BB6A', '#81C784', '#A5D6A7', '#C8E6C9'],
    };

    /** State **/
    const [monthlyActivity, setMonthlyActivity] = useState<any[]>([]);
    const [isResolved, setIsResolved] = useState(false);

    /** Computed **/
    const lastYear = true;
    const nextYear = true;

    const filters: BarChartFilters = {
      companyId: companyFilter?._id,
      loanId: loanFilter?._id,
      lenderId: lenderFilter?._id,
      category: undefined,
    };

    const dateRange = useMemo(
      () => ({
        start: dayjs(dateStore.dateRange?.start).startOf('year').toDate(),
        end: dayjs(dateStore.dateRange?.end).endOf('year').toDate(),
      }),
      [dateStore.dateRange]
    );
    const currentDate = dateStore.currentDate;

    /** Methods **/

    // Goes forward/back a block of time
    const changeDate = (modifier: number) => {
      if (!modifier) return;
      dateStore.setRangeType(DateRangeType.Yearly);
      dateStore.modifyCurrentDate(modifier);
      dateStore.calculateRange();
    };

    // Main data getting function
    const fetchData = async () => {
      try {
        const query: TGetMonthlyActivityReportRequest = {
          ...filters,
          start: dateRange.start.toISOString(),
          end: dateRange.end.toISOString(),
        };

        const data = await MonthlyActivityService.get(query);

        setMonthlyActivity(data);
        setIsResolved(true);
      } catch (err) {
        console.error(err);
      }
    };

    /** Effects **/

    useEffect(() => {
      fetchData();
    }, [dateRange, companyFilter, loanFilter, lenderFilter]);

    /** Render **/
    return (
      <section ng-controller="MonthlyActivityController" id="totalBarChart" ng-init="monthlyActivityInit()">
        <Box layout-gt-sm="row" layout-sm="column" layout-align="center center" ng-if="isResolved" marginTop="6px">
          <Box component="span" sx={{ flex: 1 }}></Box>

          <Box component="span" layout-sm="row" layout-align-sm="center center">
            <IconButton className="md-icon-button" onClick={() => changeDate(-1)} disabled={!lastYear}>
              <ChevronLeft className="md-icons material-icons" />
            </IconButton>

            <Box component="span" className="thin textZoomIn center">
              {dayjs(dateRange?.start ? dateRange.start : currentDate).format('YYYY')}
            </Box>

            <IconButton className="md-icon-button" onClick={() => changeDate(1)} disabled={!nextYear}>
              <ChevronRight className="md-icons material-icons" />
            </IconButton>
          </Box>

          <Box component="span" sx={{ flex: 1 }}></Box>
        </Box>

        {isResolved && !!monthlyActivity.length && (
          <Box>
            <BarChart
              data={monthlyActivity}
              dateRange={dateRange}
              colorRange={chartColors}
              sortReverse
              sortValue="value"
            />
          </Box>
        )}

        {isResolved && !monthlyActivity.length && (
          <Box
            ng-if="isResolved && !monthlyActivity.length"
            layout="row"
            layout-align="center center"
            className="md-headline thin pad-top-bottom"
          >
            No Activity Found.
          </Box>
        )}

        {!isResolved && (
          <Box layout="column" layout-align="center center" className="pad-top-bottom" sx={{ flex: 1 }}>
            <CircularProgress className="md-primary" />
          </Box>
        )}
      </section>
    );
  }
);
